import type { ForwardRefRenderFunction } from "react"
import React, { forwardRef, useImperativeHandle, useCallback, useEffect } from "react"
import { useSetState } from "ahooks-v2"
import classNames from "classnames"
import { InputNumber, message } from "antd"
import type { CustomizeRangeDataVO } from "@/api"
import { isNumber } from "@/util/util"
import "./index.scss"
// ==============================================
interface stateVO {
  range: number[]
  rangeError: boolean[] // 数据验证
}
interface CustomizeRangeVO {
  data: CustomizeRangeDataVO
  changeCallback?: () => void // 值改变时的回调
}
const CustomizeRange: ForwardRefRenderFunction<any, CustomizeRangeVO> = (
  { data, changeCallback },
  ref,
) => {
  const { title = "", name = "自定义", unit = "", defaultValues = [], rules = [null, null] } = data
  const [state, set] = useSetState<stateVO>({
    range: defaultValues,
    rangeError: [false, false],
  })
  const { range, rangeError } = state
  // 自定义change
  const numberRangeChange = useCallback(
    (value, index) => {
      set((prev) => {
        let numberRange = prev.range?.slice() || []
        let errorRange = prev.rangeError?.slice() || []
        if (isNumber(value)) {
          numberRange[index] = Number((value || 0).toFixed(2))
          // 根据rules判断
          if (Array.isArray(rules)) {
            const start = rules?.[0]
            const end = rules?.[1]
            if (isNumber(start)) {
              if (numberRange[index] < start) {
                numberRange[index] = start
              }
            }
            if (isNumber(end)) {
              if (numberRange[index] > end) {
                numberRange[index] = end
              }
            }
            // 左边 >= 右边时
            if (numberRange[0] >= numberRange[1]) {
              errorRange[index] = true
            } else {
              errorRange[index] = false
              // 判断另一个值是否符合要求
              const nextIndex = index === 0 ? 1 : 0
              // 根据rules判断
              if (isNumber(start) && isNumber(end)) {
                if (numberRange[nextIndex] >= start && numberRange[nextIndex] <= end) {
                  errorRange[nextIndex] = false
                }
              } else if (isNumber(start)) {
                if (numberRange[nextIndex] >= start) {
                  errorRange[nextIndex] = false
                }
              } else if (isNumber(end)) {
                if (numberRange[nextIndex] <= end) {
                  errorRange[nextIndex] = false
                }
              }
            }
          } else if (rules instanceof Function) {
            if (rules(numberRange[index])) {
              errorRange[index] = false
              // 左边 >= 右边时
              if (numberRange[0] >= numberRange[1]) {
                errorRange[index] = true
              } else {
                errorRange[index] = false
                // 判断另一个值是否符合要求
                const nextIndex = index === 0 ? 1 : 0
                // 注册时间
                if (rules(numberRange[nextIndex])) {
                  errorRange[nextIndex] = false
                }
              }
            } else {
              errorRange[index] = true
            }
          }
        } else {
          numberRange = []
          errorRange = [false, false]
        }
        return {
          range: numberRange,
          rangeError: errorRange,
        }
      })
      changeCallback && changeCallback()
    },
    [set, rules, changeCallback],
  )
  // rules错误提示信息
  const rulesCallback = useCallback((msg) => {
    if (msg) message.error(msg)
  }, [])
  // 自定义校验
  const rangeVerify = useCallback(() => {
    const newrange = Array.from(range)
    const start = newrange?.[0]
    const end = newrange?.[1]
    // 自定义填写完整
    if (range?.length && ((!start && start !== 0) || (!end && end !== 0))) {
      message.error(`${title}请将自定义填写完整`)
      return false
    }
    // rules
    if (range?.length === 2 && rules instanceof Function) {
      if (!rules(start, rulesCallback) || !rules(end, rulesCallback)) {
        return false
      }
    }
    // rangeError
    if (rangeError?.indexOf(true) >= 0) {
      // start < end
      if (start >= end) {
        message.error(`${title}起始数值应小于结束数值`)
        return false
      }
      message.error(`${title}自定义填写存在错误`)
      return false
    }
    return true
  }, [range, rangeError, title, rules, rulesCallback])
  // 获取自定义
  const getValues = useCallback(() => {
    return state.range
  }, [state.range])
  // 设置自定义
  const setValues = useCallback(
    (values) => {
      set({
        range: values,
        rangeError: [false, false],
      })
    },
    [set],
  )
  // 清空自定义
  const clearValues = useCallback(() => {
    set({
      range: [],
      rangeError: [false, false],
    })
  }, [set])
  // 传递方法到父组件
  useImperativeHandle(ref, () => ({
    rangeVerify,
    getValues,
    setValues,
    clearValues,
  }))
  // 设置值
  useEffect(() => {
    set({
      range: defaultValues,
      rangeError: [false, false],
    })
  }, [defaultValues, set])
  return data ? (
    <div className="customize-range">
      {name}：&nbsp;
      <InputNumber
        key="start"
        className={classNames({
          error: rangeError?.[0],
        })}
        value={range?.[0]}
        onChange={(value) => numberRangeChange(value, 0)}
      />
      &nbsp;-&nbsp;
      <InputNumber
        key="end"
        className={classNames({
          error: rangeError?.[1],
        })}
        value={range?.[1]}
        onChange={(value) => numberRangeChange(value, 1)}
      />
      &nbsp;{unit}
    </div>
  ) : null
}

export default forwardRef(CustomizeRange)
